if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.button = this.querySelector('.sticky-add-to-cart__button');
        this.buttonLabel = this.button.querySelector('span');
        this.spinner = this.button.querySelector('.loading__spinner');
        this.priceDisplay = this.querySelector('.sticky-add-to-cart__price');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
        this.handleStructureChange = this.handleStructureChange.bind(this);
        this.syncState = this.syncState.bind(this);
        this.syncPrice = this.syncPrice.bind(this);
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.button.addEventListener('click', this.handleClick);
        this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
        if (this.mobileQuery.matches) this.activate();
      }

      disconnectedCallback() {
        this.button.removeEventListener('click', this.handleClick);
        this.mobileQuery.removeEventListener('change', this.handleBreakpointChange);
        this.deactivate();
      }

      handleBreakpointChange(event) {
        if (event.matches) {
          this.activate();
        } else {
          this.deactivate();
        }
      }

      // Re-acquires the live Dawn nodes and (re)starts the observers that mirror them. Safe to
      // call repeatedly -- deactivate() always runs first when re-binding after a DOM swap.
      activate() {
        const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        const liveButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!productInfo || !liveButton) {
          this.setAttribute('hidden', '');
          return;
        }

        this.productInfo = productInfo;
        this.liveButton = liveButton;
        this.priceSource = document.getElementById(`price-${this.sectionId}`);

        this.syncState();
        this.syncPrice();

        this.intersectionObserver = new IntersectionObserver(this.handleIntersection, { threshold: 0 });
        this.intersectionObserver.observe(liveButton);

        this.stateObserver = new MutationObserver(this.syncState);
        this.stateObserver.observe(liveButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });

        if (this.priceSource) {
          this.priceObserver = new MutationObserver(this.syncPrice);
          this.priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
        }

        // Watches for a full product-info subtree swap (Section Rendering API variant updates,
        // Theme Editor section reload) so stale node references never linger.
        this.structureObserver = new MutationObserver(this.handleStructureChange);
        this.structureObserver.observe(productInfo, { childList: true, subtree: true });
      }

      deactivate() {
        this.intersectionObserver?.disconnect();
        this.stateObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.structureObserver?.disconnect();
        this.intersectionObserver = null;
        this.stateObserver = null;
        this.priceObserver = null;
        this.structureObserver = null;
        this.productInfo = null;
        this.liveButton = null;
        this.priceSource = null;
        this.setAttribute('hidden', '');
        document.body.classList.remove('has-sticky-add-to-cart');
      }

      handleStructureChange() {
        const stillConnected =
          this.liveButton && document.contains(this.liveButton) && this.productInfo && document.contains(this.productInfo);
        if (stillConnected) return;

        this.deactivate();
        if (this.mobileQuery.matches) this.activate();
      }

      handleIntersection(entries) {
        const entry = entries[entries.length - 1];
        // Sticky bar is only useful once the real, canonical button has scrolled out of view.
        const visible = !entry.isIntersecting;
        this.toggleAttribute('hidden', !visible);
        // Reserves page-bottom clearance only while the bar is actually covering it -- this way
        // the accommodation follows the visitor down the whole page (accordions, related
        // products, footer), not just the boundary right after this PDP section.
        document.body.classList.toggle('has-sticky-add-to-cart', visible);
      }

      syncState() {
        if (!this.liveButton) return;

        const disabled = this.liveButton.disabled || this.liveButton.getAttribute('aria-disabled') === 'true';
        this.button.disabled = disabled;

        const liveLabel = this.liveButton.querySelector('span');
        if (liveLabel && this.buttonLabel) this.buttonLabel.textContent = liveLabel.textContent;

        const loading = this.liveButton.classList.contains('loading');
        this.button.classList.toggle('loading', loading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
      }

      syncPrice() {
        if (!this.priceSource || !this.priceDisplay) return;
        this.priceDisplay.innerHTML = this.priceSource.innerHTML;
      }

      handleClick() {
        // Re-query at click time -- never act on a cached reference, per Architecture v2 Section 5.
        const liveButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!liveButton) return;

        const disabled = liveButton.disabled || liveButton.getAttribute('aria-disabled') === 'true';
        if (disabled) return;

        liveButton.click();
      }
    }
  );
}
